import { useEffect, useState } from "react";

interface PasswordStrengthIndicatorProps {
  password: string;
}

export function PasswordStrengthIndicator({ password }: PasswordStrengthIndicatorProps) {
  const [strength, setStrength] = useState(0);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!password) {
      setStrength(0);
      setMessage("");
      return;
    }

    let score = 0;
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      numbers: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };

    // Calculate score
    if (checks.length) score++;
    if (checks.lowercase && checks.uppercase) score++;
    if (checks.numbers) score++;
    if (checks.special) score++;
    if (password.length >= 12) score++;

    setStrength(score);

    // Set message based on score
    const messages = [
      "",
      "Weak",
      "Fair",
      "Good",
      "Strong",
      "Very Strong"
    ];
    setMessage(messages[score]);
  }, [password]);

  const getColor = () => {
    const colors = [
      "bg-gray-300",
      "bg-red-500",
      "bg-orange-500",
      "bg-yellow-500",
      "bg-green-500",
      "bg-green-600"
    ];
    return colors[strength];
  };

  if (!password) return null;

  return (
    <div className="mt-2">
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((level) => (
          <div
            key={level}
            className={`h-1 flex-1 rounded-full transition-colors ${
              level <= strength ? getColor() : "bg-gray-200"
            }`}
          />
        ))}
      </div>
      {message && (
        <p className={`text-xs mt-1 ${
          strength <= 2 ? "text-red-500" : 
          strength === 3 ? "text-yellow-600" : 
          "text-green-600"
        }`}>
          Password strength: {message}
        </p>
      )}
    </div>
  );
}